import { isPresent } from "@apl-digital/utils";
import { createSelector } from "@reduxjs/toolkit";
import { endOfDay, isBefore } from "date-fns";

import { isStatus } from "@/store/utils";

import {
  mapOrderDataToOrderDataWithGrouping,
  mapOrderStatusResponseToOrderData,
} from "./mappers";
import { selectOrders } from "./slice";
import { sortOrdersByCreatedDateOrSelected } from "./utils";

export const selectOrderData = createSelector([selectOrders], (orders) =>
  orders.map((order) =>
    isStatus(order, ["stale", "stalePending", "succeeded", "orderMoved"])
      ? { ...order, state: mapOrderStatusResponseToOrderData(order.state) }
      : order,
  ),
);

const selectActiveOrders = createSelector([selectOrderData], (orders) =>
  orders.filter(
    (order) =>
      (isStatus(order, ["stale", "stalePending", "succeeded"]) &&
        !order.state.isComplete) ||
      isStatus(order, ["idle", "pending", "failedPending", "failed"]),
  ),
);

const selectInactiveOrders = createSelector([selectOrderData], (orders) =>
  orders.filter(
    (order) =>
      (isStatus(order, ["stale", "stalePending", "succeeded"]) &&
        order.state.isComplete) ||
      isStatus(order, ["notFound", "orderMoved"]),
  ),
);

export const selectActiveOrdersWithGrouping = createSelector(
  [
    selectActiveOrders,
    (_state, selectedOrderId: string | undefined) => selectedOrderId,
  ],
  (orders, selectedOrderId) =>
    orders
      .map((order) =>
        isStatus(order, ["stale", "stalePending", "succeeded", "orderMoved"])
          ? {
              ...order,
              state: mapOrderDataToOrderDataWithGrouping(order.state),
            }
          : order,
      )
      .toSorted((a, b) =>
        sortOrdersByCreatedDateOrSelected(a.state, b.state, selectedOrderId),
      ),
);

export const selectInactiveOrdersWithGrouping = createSelector(
  [
    selectInactiveOrders,
    (_state, selectedOrderId: string | undefined) => selectedOrderId,
  ],
  (orders, selectedOrderId) =>
    orders
      .map((order) =>
        isStatus(order, ["stale", "stalePending", "succeeded", "orderMoved"])
          ? {
              ...order,
              state: mapOrderDataToOrderDataWithGrouping(order.state),
            }
          : order,
      )
      .toSorted((a, b) =>
        sortOrdersByCreatedDateOrSelected(a.state, b.state, selectedOrderId),
      ),
);

export const selectOrderWithGroupingByBarcodeWithoutChecksum = createSelector(
  [
    selectOrderData,
    (_state, barcodeWithoutChecksum: string | undefined) =>
      barcodeWithoutChecksum,
  ],
  (orders, barcodeWithoutChecksum) => {
    const existingOrder = orders.find(
      (order) => order.state.BarcodeWithoutChecksum === barcodeWithoutChecksum,
    );

    if (existingOrder) {
      return isStatus(existingOrder, [
        "stale",
        "stalePending",
        "succeeded",
        "orderMoved",
      ])
        ? {
            ...existingOrder,
            state: mapOrderDataToOrderDataWithGrouping(existingOrder.state),
          }
        : existingOrder;
    }

    return;
  },
);

export const selectActiveAndSelectedOrdersWithGrouping = createSelector(
  [
    selectActiveOrdersWithGrouping,
    (state, selectedOrderId: string | undefined) =>
      selectOrderWithGroupingByBarcodeWithoutChecksum(state, selectedOrderId),
  ],
  (activeOrders, selectedOrder) => {
    const orders = [...activeOrders];

    if (isPresent(selectedOrder)) {
      const selectedOrderIndex = orders.findIndex(
        (order) =>
          order.state.BarcodeWithoutChecksum ===
          selectedOrder.state.BarcodeWithoutChecksum,
      );

      if (selectedOrderIndex === -1) {
        orders.unshift(selectedOrder);
      } else {
        orders.splice(selectedOrderIndex, 1);
        orders.unshift(selectedOrder);
      }
    }

    return orders;
  },
);

export const selectOrdersForRefresh = createSelector([selectOrders], (orders) =>
  orders
    .filter(
      (order) =>
        isStatus(order, ["idle", "stale", "failed"]) &&
        !(isStatus(order, "failed") && order.retryCount > 3),
    )
    .map((order) => order.state.BarcodeWithoutChecksum),
);

export const selectActiveOrdersCount = createSelector(
  selectActiveOrders,
  (orders) => orders.length,
);

export const selectNoneExpiredOrders = createSelector(
  [selectOrders, selectActiveOrders, selectInactiveOrders],
  (orders, activeOrders, inactiveOrders) => {
    const noneExpiredBarCodes = new Set(
      [
        ...inactiveOrders.filter(
          (order) =>
            (isStatus(order, ["stale", "stalePending", "succeeded"]) &&
              isPresent(order.state.dttmLastStatusChange) &&
              isBefore(
                new Date(),
                endOfDay(new Date(order.state.dttmLastStatusChange)),
              )) ||
            isStatus(order, ["idle", "pending", "failedPending", "failed"]),
        ),
        ...activeOrders,
      ].map((order) => order.state.BarcodeWithoutChecksum),
    );

    return orders.filter((order) =>
      noneExpiredBarCodes.has(order.state.BarcodeWithoutChecksum),
    );
  },
);

export const selectIsOrderRegistered = createSelector(
  [
    selectOrders,
    (_state, barcodeWithoutChecksum: string | undefined) =>
      barcodeWithoutChecksum,
  ],
  (orders, barcodeWithoutChecksum) =>
    orders.some(
      (order) => order.state.BarcodeWithoutChecksum === barcodeWithoutChecksum,
    ),
);
